/**
 * useActivityFeed — Sprint 9 / neue Backend-API
 *
 * Fetch + Pagination + Filter-State für den Activity-Feed.
 * Nutzt TanStack Query useQuery mit Query-Key ['activities', actionType, page].
 * Filter-Wechsel resettet auf Seite 1.
 * Load-More haengt neue Items an die akkumulierte Liste (meta.has_more steuert Button).
 *
 * FIX-VALIDATION-F014: Request-Storm Prevention
 * - refetchOnWindowFocus: false — verhindert Refetch-Sturm bei Tab-Wechsel
 * - refetchOnReconnect: false — verhindert Refetch nach Netzwerk-Reconnect
 * - retry: 1 — begrenzt Retry-Versuche
 * - staleTime: 30_000 — 30s Cache-Gueltigkeit
 */
import { useState, useCallback, useReducer } from 'react'
import { useQuery } from '@tanstack/react-query'
import { fetchActivities } from '../api/activityApi'
import type { ActivityGroup } from '../api/activityApi'

export type ActivityFeedState = {
  items: ActivityGroup[]
  hasMore: boolean
  loading: boolean
  error: boolean
  is404: boolean
  actionType: string | null
  /** Serverseitiges Free/Premium-Zeitfenster in Tagen (meta.day_limit), null wenn unbekannt. */
  dayLimit: number | null
  /** Tatsaechlich angewendete untere Zeitgrenze (ISO8601, meta.window_start). */
  windowStart: string | null
  setFilter: (type: string | null) => void
  loadMore: () => void
  refetch: () => void
}

type AccumulatorAction =
  | { type: 'reset'; items: ActivityGroup[] }
  | { type: 'append'; items: ActivityGroup[]; existingKeys: Set<string> }
  | { type: 'clear' }

function accumulatorReducer(state: ActivityGroup[], action: AccumulatorAction): ActivityGroup[] {
  switch (action.type) {
    case 'reset':
      return action.items
    case 'append': {
      const fresh = action.items.filter((i) => !action.existingKeys.has(i.group_key))
      return [...state, ...fresh]
    }
    case 'clear':
      return []
  }
}

export type ActivityFeedParams = {
  since?: string
  until?: string
  hotspot_id?: number
  /**
   * R5/FD: Serverseitiger Ereignistyp-Filter. Einzelwert ("heartbeat") oder
   * Komma-Liste ("heartbeat,speedtest") — das Backend akzeptiert beides.
   * Wird der Wert gesetzt (auch null), hat er Vorrang vor dem internen
   * setFilter-State; `undefined` laesst den internen State unveraendert.
   */
  action_type?: string | null
}

export function useActivityFeed(params: ActivityFeedParams = {}): ActivityFeedState {
  const [page, setPage] = useState(1)
  const [actionType, setActionType] = useState<string | null>(null)
  const [accumulatedItems, dispatch] = useReducer(accumulatorReducer, [])
  const [lastFetchKey, setLastFetchKey] = useState('')

  // Ein von aussen gesetzter action_type (Ereignistyp-Dropdown / Kategorie-Tab)
  // gewinnt gegen den internen setFilter-State.
  const outerActionType = params.action_type

  // Zeitraum-/Hotspot-/Filter-Wechsel: zurueck auf Seite 1 und Accumulator leeren,
  // sonst wirkt ein Wechsel auf Seite > 1 faelschlich leer.
  // Reset wird synchron im Renderpfad abgeleitet (React-Pattern
  // "state reset on key change"), nicht per Effect — sonst kann bei warmem
  // Cache ein Frame mit gemischten Zeitraeumen bzw. ein ueberfluessiger
  // page>1-Request entstehen.
  const effectiveActionType = outerActionType !== undefined ? outerActionType : actionType

  const paramsKey = `${params.since ?? ''}|${params.until ?? ''}|${params.hotspot_id ?? ''}|${effectiveActionType ?? ''}`
  const [prevParamsKey, setPrevParamsKey] = useState(paramsKey)
  const paramsChanged = prevParamsKey !== paramsKey
  if (paramsChanged) {
    setPrevParamsKey(paramsKey)
    setPage(1)
    setLastFetchKey('')
    dispatch({ type: 'clear' })
  }
  const effectivePage = paramsChanged ? 1 : page

  // FIX-VALIDATION-F014: Prevent request storm
  const { data, isFetching, isError, refetch: queryRefetch } = useQuery({
    queryKey: ['activities', effectiveActionType, effectivePage, params.since, params.until, params.hotspot_id] as const,
    queryFn: () =>
      fetchActivities({
        page: effectivePage,
        per_page: 20,
        action_type: effectiveActionType ?? undefined,
        since: params.since,
        until: params.until,
        hotspot_id: params.hotspot_id,
      }),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 1,
  })

  const is404 = data?.status === 404 && data?.fromMock === true
  const hasMore = data?.data?.meta?.has_more ?? false
  const dayLimit = data?.data?.meta?.day_limit ?? null
  const windowStart = data?.data?.meta?.window_start ?? null

  const newItems = data?.data?.data ?? []
  // Bei Param-Wechsel gelten Accumulator und lastFetchKey in diesem Render
  // bereits als geleert (die Reset-Dispatches oben greifen erst im naechsten Render).
  const effectiveItems = paramsChanged ? [] : accumulatedItems
  const effectiveLastFetchKey = paramsChanged ? '' : lastFetchKey
  const fetchKey = `${effectiveActionType ?? 'all'}-${effectivePage}-${paramsKey}-${data?.data?.meta?.total ?? 0}`
  // Kein `newItems.length > 0`-Guard: eine leere Antwort MUSS die Liste leeren,
  // sonst bleiben Items eines vorherigen Filters stehen (Stale-List-Bug).
  if (data?.data && fetchKey !== effectiveLastFetchKey) {
    // Sync-Update bei Render-Zeit: erlaubt in nicht-Event-Handlern per React-Docs
    // wenn der Zustand direkt vom gerenderten Output abhaengt (abgeleiteter State).
    if (effectivePage === 1) {
      dispatch({ type: 'reset', items: newItems })
    } else {
      const existingKeys = new Set(effectiveItems.map((i) => i.group_key))
      dispatch({ type: 'append', items: newItems, existingKeys })
    }
    setLastFetchKey(fetchKey)
  }

  const setFilter = useCallback((type: string | null) => {
    dispatch({ type: 'clear' })
    setLastFetchKey('')
    setActionType(type)
    setPage(1)
  }, [])

  const loadMore = useCallback(() => {
    setPage((p) => p + 1)
  }, [])

  const refetch = useCallback(() => {
    queryRefetch()
  }, [queryRefetch])

  return {
    items: effectiveItems,
    hasMore,
    loading: isFetching,
    error: isError || (!isFetching && !!data?.error),
    is404,
    actionType: effectiveActionType,
    dayLimit,
    windowStart,
    setFilter,
    loadMore,
    refetch,
  }
}
